import { useRouter } from 'next/router'
import {
  Flex,
  Box,
  FormControl,
  FormLabel,
  Input,
  Button,
  Stack,
  Heading,
  Link,
  Spacer,
  Icon,
} from '@chakra-ui/react'
import { HiOutlineLogin } from 'react-icons/hi'
import colors from 'theme/colors'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AuthenticationLeftImage } from 'components/ui/AuthenticationLeftImage'
import { ResponseError, useErrorHandler } from 'lib/apis/gigBizApi/errorHandler'
import { setAuthToken } from 'lib/apis/gigBizApi/config'
import { apiClient } from 'lib/apis'
import useToast from 'lib/toast'
import { signinFormPayload } from 'types/signin'

const Signin = () => {
  const toast = useToast()
  const router = useRouter()
  const queryClient = useQueryClient()
  const errorHandler = useErrorHandler()
  const { handleSubmit, register } = useForm<signinFormPayload>()

  const requestSignin = async (data: signinFormPayload) => {
    return apiClient.gigBizApi.authentications.signin(data)
  }

  const { isLoading, mutate } = useMutation(requestSignin, {
    onSuccess: (res) => {
      if (!res.data.content) throw new Error()
      setAuthToken(res.data.content.token)
      queryClient.invalidateQueries()
      toast({
        status: 'success',
        title: 'ログインしました',
      })
      router.push('/')
    },
    onError: (error: ResponseError) => {
      errorHandler.default(error)
    },
  })

  const submit = handleSubmit((data: signinFormPayload) => {
    mutate(data)
  })

  return (
    <Stack minH={'100vh'} direction={{ base: 'column', md: 'row' }}>
      <AuthenticationLeftImage />
      <Flex
        p={8}
        flex={1}
        pb={[20, 20, 0]}
        align={'center'}
        justify={'center'}
        backgroundColor={colors.bg}
      >
        <Stack spacing={4} w={'full'} maxW={'md'}>
          <Heading fontWeight="bold" fontSize={'4xl'} mb="5">
            ログイン
          </Heading>

          <form onSubmit={submit}>
            <FormControl>
              <Box mb="6">
                <FormLabel htmlFor="identifier">企業ID</FormLabel>
                <Input
                  id="identifier"
                  required
                  type="text"
                  placeholder="半角英数字のみ"
                  backgroundColor="white"
                  {...register('identifier')}
                />
              </Box>
              <Box mb="6">
                <FormLabel htmlFor="email">メールアドレス</FormLabel>
                <Input
                  id="email"
                  required
                  type="email"
                  placeholder="sample@qlean.jp"
                  backgroundColor="white"
                  {...register('email')}
                />
              </Box>
              <Box mb="6">
                <FormLabel htmlFor="password">パスワード</FormLabel>
                <Input
                  id="password"
                  required
                  type="password"
                  placeholder="半角英数字6文字以上"
                  backgroundColor="white"
                  {...register('password')}
                />
              </Box>
              <Flex mt="10" direction={{ base: 'column', md: 'row' }}>
                <Button
                  colorScheme="brandSecondary"
                  type="submit"
                  size="lg"
                  w={['100%', '100%', '186px']}
                  isLoading={isLoading}
                  data-testid={'signin-button'}
                >
                  <Icon mr="2" as={HiOutlineLogin} />
                  ログイン
                </Button>
                <Spacer></Spacer>
                <Box mt={[10, 10, 0]}>
                  <Link
                    textDecoration="underline"
                    _hover={{ textDecoration: 'none' }}
                    color="gray.500"
                    fontSize="sm"
                    onClick={() => {
                      router.push('password_forget')
                    }}
                  >
                    パスワードを忘れた方はこちら
                  </Link>
                </Box>
              </Flex>
            </FormControl>
          </form>
        </Stack>
      </Flex>
    </Stack>
  )
}

export default Signin
